/**
 * asciiThoughtFrames.ts — vibe-designer (rebuilt 2026-07-29)
 *
 * The in-thread "thinking" placeholder's animation data: a set of looping
 * Braille dot "movies" shown where Pi's next message will appear (injected next
 * to pi's streaming cursor by asciiThoughtInjector). Rebuilt from the earlier
 * 6×12 block (which had to be `zoom`-shrunk, dimming + mis-placing it) into a
 * NATIVE-SMALL 1-row × 8-char canvas (16×4 dots) that renders at the chat font
 * size — one line tall, full brightness, no zoom.
 *
 * Braille: each cell (U+2800..U+28FF) packs a 2-wide × 4-tall dot grid, so the
 * 8-char row is a 16×4 DOT canvas — a compact, crisp horizontal animation.
 *
 * Each movie ships TWO parallel channels:
 *   - `frames`      : the Braille strings (ASCII_ROWS lines × ASCII_COLS glyphs).
 *   - `alphaFrames` : per-cell brightness 0..1 (comet tail / wave falloff). The
 *                     controller renders each cell as a span at that opacity;
 *                     concatenated span text still equals the frame string.
 *
 * Frames are precomputed deterministically at module load (pure trig over a dot
 * grid — no Date/random), so the set is stable and unit-testable.
 */

/** Braille character rows per frame — 1 row (4 dots tall) = one text line. */
export const ASCII_ROWS = 1;
/** Braille character columns per frame. */
export const ASCII_COLS = 8;
/** Dot canvas: ASCII_COLS*2 wide × ASCII_ROWS*4 tall. */
const DOT_W = ASCII_COLS * 2; // 16
const DOT_H = ASCII_ROWS * 4; // 4
/** Frames per movie loop. */
const FRAMES = 16;

const CX = (DOT_W - 1) / 2; // 7.5
const CY = (DOT_H - 1) / 2; // 1.5
const TAU = Math.PI * 2;

interface DotCanvas {
  d: Uint8Array; // lit flag, row-major
  a: Float32Array; // per-dot brightness 0..1
}

function canvas(): DotCanvas {
  return { d: new Uint8Array(DOT_W * DOT_H), a: new Float32Array(DOT_W * DOT_H) };
}

function setDot(g: DotCanvas, x: number, y: number, alpha = 1): void {
  const xi = Math.round(x);
  const yi = Math.round(y);
  if (xi < 0 || xi >= DOT_W || yi < 0 || yi >= DOT_H) return;
  const i = yi * DOT_W + xi;
  g.d[i] = 1;
  if (alpha > g.a[i]) g.a[i] = alpha; // keep the brightest contributor
}

/** Filled disc of radius r centered at (cx, cy). */
function disc(g: DotCanvas, cx: number, cy: number, r: number, alpha = 1): void {
  const r2 = r * r;
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) {
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy <= r2) setDot(g, x, y, alpha);
    }
  }
}

/** Ring (annulus) of radius r, half-thickness t. */
function ring(g: DotCanvas, cx: number, cy: number, r: number, t: number, alpha = 1): void {
  const outer = (r + t) * (r + t);
  const inner = Math.max(0, r - t) * Math.max(0, r - t);
  for (let y = Math.floor(cy - r - t); y <= Math.ceil(cy + r + t); y++) {
    for (let x = Math.floor(cx - r - t); x <= Math.ceil(cx + r + t); x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 <= outer && d2 >= inner) setDot(g, x, y, alpha);
    }
  }
}

// Braille dot-bit layout within a 2×4 cell (col, row) → bit.
const BRAILLE_BITS = [
  [0x01, 0x02, 0x04, 0x40], // left column, rows 0..3
  [0x08, 0x10, 0x20, 0x80], // right column, rows 0..3
];

/** Pack a dot canvas into ASCII_ROWS lines of ASCII_COLS Braille chars. */
function pack(g: DotCanvas): string {
  const lines: string[] = [];
  for (let cr = 0; cr < ASCII_ROWS; cr++) {
    let lineStr = "";
    for (let cc = 0; cc < ASCII_COLS; cc++) {
      let mask = 0;
      for (let sub = 0; sub < 2; sub++) {
        for (let row = 0; row < 4; row++) {
          const x = cc * 2 + sub;
          const y = cr * 4 + row;
          if (g.d[y * DOT_W + x]) mask |= BRAILLE_BITS[sub][row];
        }
      }
      lineStr += String.fromCharCode(0x2800 + mask);
    }
    lines.push(lineStr);
  }
  return lines.join("\n");
}

/** Per-cell brightness (max lit-dot alpha in each cell), row-major, length ROWS*COLS. */
function packAlpha(g: DotCanvas): number[] {
  const out: number[] = [];
  for (let cr = 0; cr < ASCII_ROWS; cr++) {
    for (let cc = 0; cc < ASCII_COLS; cc++) {
      let amax = 0;
      for (let sub = 0; sub < 2; sub++) {
        for (let row = 0; row < 4; row++) {
          const x = cc * 2 + sub;
          const y = cr * 4 + row;
          const i = y * DOT_W + x;
          if (g.d[i] && g.a[i] > amax) amax = g.a[i];
        }
      }
      out.push(amax);
    }
  }
  return out;
}

type FrameFn = (t: number) => DotCanvas; // t = frame index 0..FRAMES-1

// ===================== the six movies =====================

/** Comet — a bright head sweeping a flat ellipse, with a fading tail. */
const comet: FrameFn = (t) => {
  const g = canvas();
  const N = FRAMES;
  const TAIL = 7;
  for (let k = 0; k < TAIL; k++) {
    const tt = ((t - k) % N + N) % N;
    const ang = (tt / N) * TAU;
    const x = CX + Math.cos(ang) * 6.6;
    const y = CY + Math.sin(ang) * 1.5;
    const alpha = 1 - k / (TAIL + 1);
    disc(g, x, y, k === 0 ? 1.1 : 0.6, alpha);
  }
  return g;
};

/** Wave — a travelling sine of dots, brightness riding the crest. */
const wave: FrameFn = (t) => {
  const g = canvas();
  for (let x = 0; x < DOT_W; x++) {
    const ph = (x / DOT_W) * TAU * 1.5 - (t / FRAMES) * TAU;
    const y = CY + Math.sin(ph) * 1.5;
    const alpha = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(ph));
    setDot(g, x, y, alpha);
  }
  return g;
};

/** Orbit — a satellite dot circling a soft ring. */
const orbit: FrameFn = (t) => {
  const g = canvas();
  ring(g, CX, CY, 1.4, 0.55, 0.5);
  const ang = (t / FRAMES) * TAU;
  disc(g, CX + Math.cos(ang) * 5.2, CY + Math.sin(ang) * 1.5, 1, 1);
  return g;
};

/** Pulse — a breathing ring around a steady core. */
const pulse: FrameFn = (t) => {
  const g = canvas();
  const phase = 0.5 + 0.5 * Math.sin((t / FRAMES) * TAU);
  const r = 1.2 + 4.6 * phase;
  ring(g, CX, CY, r, 0.5, 0.55 + 0.45 * phase);
  disc(g, CX, CY, 0.8, 1);
  return g;
};

/** Runner — a compact cluster bouncing along a baseline. */
const runner: FrameFn = (t) => {
  const g = canvas();
  const head = (t / FRAMES) * DOT_W;
  for (let k = 0; k < 4; k++) {
    const x = head - k * 1.7;
    const bx = ((x % DOT_W) + DOT_W) % DOT_W;
    const y = CY - Math.abs(Math.sin((x / DOT_W) * TAU)) * 1.5;
    disc(g, bx, y, k === 0 ? 1 : 0.6, 1 - k * 0.22);
  }
  return g;
};

/** Stream — dots flowing in and draining left→right (progress-like). */
const stream: FrameFn = (t) => {
  const g = canvas();
  for (let x = 0; x < DOT_W; x++) {
    const ph = ((x - t * 0.9) / DOT_W) * TAU;
    const on = Math.sin(ph);
    if (on > -0.2) setDot(g, x, CY, 0.4 + 0.6 * (0.5 + 0.5 * on));
  }
  return g;
};

export interface AsciiMovie {
  id: string;
  name: string;
  /** Loop frames: ASCII_ROWS lines of ASCII_COLS Braille chars, '\n'-joined. */
  frames: string[];
  /** Per-frame, per-cell brightness 0..1 (row-major), parallel to `frames`. */
  alphaFrames: number[][];
}

function build(id: string, name: string, fn: FrameFn): AsciiMovie {
  const frames: string[] = [];
  const alphaFrames: number[][] = [];
  for (let t = 0; t < FRAMES; t++) {
    const c = fn(t);
    frames.push(pack(c));
    alphaFrames.push(packAlpha(c));
  }
  return { id, name, frames, alphaFrames };
}

/** The rotating movie set. Precomputed at load — deterministic + stable.
 *  Comet is first so it is the natural head of the rotation (and the default). */
export const ASCII_THOUGHT_MOVIES: readonly AsciiMovie[] = [
  build("comet", "Comet", comet),
  build("wave", "Wave", wave),
  build("orbit", "Orbit", orbit),
  build("pulse", "Pulse", pulse),
  build("runner", "Runner", runner),
  build("stream", "Stream", stream),
];

/**
 * Pick a movie by a non-negative seed (e.g. a turn counter), wrapping around the
 * set so consecutive turns cycle through the different animations.
 */
export function pickMovie(seed: number): AsciiMovie {
  const n = ASCII_THOUGHT_MOVIES.length;
  const i = ((Math.floor(seed) % n) + n) % n;
  return ASCII_THOUGHT_MOVIES[i];
}
